// SupportTicketModal
// -------------------
// In-app support ticket form, available to every authenticated user from the
// account menu. Collects a category + message only — the user's name and email
// are attached server-side from their Cognito session (never sent from the
// client), so we show a read-only "sent as ..." line rather than editable
// identity fields.
//
// Close behavior: a support message is typed once and is often long, so a
// stray backdrop click never discards it. Deliberate dismissals (X, Cancel,
// Escape) close straight away without a confirmation prompt — the user meant
// it. Nothing closes while a submission is in flight, or the user would not
// learn whether it sent and would resubmit.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { LifeBuoy, X } from "lucide-react";
import { FormField } from "@/components/ui/form-field";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useModalAccessibility } from "@/utils/useModalAccessibility";
import UserClass from "@/models/user";
import {
  submitSupportTicket,
  SUPPORT_CATEGORIES,
  SUPPORT_MESSAGE_MIN_LENGTH,
  SUPPORT_MESSAGE_MAX_LENGTH,
  type SupportCategory,
} from "@/services/support.service";

interface SupportTicketModalProps {
  open: boolean;
  onClose: () => void;
  user: UserClass;
}

const DEFAULT_CATEGORY: SupportCategory = "QUESTION";

// How the user asked to close. A backdrop click is the one path that is
// routinely accidental, so it is the one path that is ignored when there is
// unsent text. Every other path is deliberate and closes immediately.
type CloseRequestSource = "backdrop" | "dismiss";

const GENERIC_SUBMIT_ERROR = "Could not submit your ticket. Please try again.";

export default function SupportTicketModal({ open, onClose, user }: SupportTicketModalProps) {
  const dialogContainerRef = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState<SupportCategory>(DEFAULT_CATEGORY);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ticketReference, setTicketReference] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // The message the user would actually send. Everything downstream — the
  // counter, the enable/disable rule, and the payload — reads this one value,
  // so what the counter shows and what the server receives cannot disagree.
  const trimmedMessage = message.trim();
  const hasUnsentText = trimmedMessage.length > 0 && !submitted;

  const requestClose = useCallback(
    (source: CloseRequestSource) => {
      // Never close mid-flight: the request would still complete, the user
      // would not see the outcome, and they would raise a duplicate ticket.
      if (submitting) return;
      // A click outside is the one dismissal that is routinely accidental, so
      // it is ignored while there is text worth keeping. X, Cancel and Escape
      // are deliberate and close without further questions.
      if (source === "backdrop" && hasUnsentText) return;
      onClose();
    },
    [submitting, hasUnsentText, onClose]
  );

  const handleEscapeClose = useCallback(() => requestClose("dismiss"), [requestClose]);

  // Escape closes; focus shifts into the modal on open and returns on close.
  useModalAccessibility(open, handleEscapeClose, dialogContainerRef);

  // Reset transient state each time the modal opens so a previous submission's
  // success/error never leaks into a fresh ticket.
  useEffect(() => {
    if (open) {
      setCategory(DEFAULT_CATEGORY);
      setMessage("");
      setSubmitting(false);
      setErrorMessage(null);
      setTicketReference(null);
      setSubmitted(false);
    }
  }, [open]);

  if (!open) return null;

  const isBelowMinimumLength = trimmedMessage.length < SUPPORT_MESSAGE_MIN_LENGTH;
  const canSubmit =
    !submitting &&
    !isBelowMinimumLength &&
    trimmedMessage.length <= SUPPORT_MESSAGE_MAX_LENGTH;

  const reporterEmail = user?.emailaddress || "";
  const reporterName =
    [user?.given_name, user?.family_name].filter(Boolean).join(" ") || user?.displayName || "";

  // Tell the user why Send is disabled instead of leaving them to guess.
  const messageHelperText = isBelowMinimumLength
    ? `${trimmedMessage.length}/${SUPPORT_MESSAGE_MAX_LENGTH}, at least ${SUPPORT_MESSAGE_MIN_LENGTH} characters`
    : `${trimmedMessage.length}/${SUPPORT_MESSAGE_MAX_LENGTH}`;

  const handleMessageChange = (changeEvent: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(changeEvent.target.value);
    // A previous failure should not sit over a message the user has since edited.
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    const result = await submitSupportTicket({
      category,
      message: trimmedMessage,
      // Breadcrumb for triage — the app route the user raised the ticket from.
      appContext: window.location.pathname,
    });
    setSubmitting(false);
    if (result.ok) {
      setTicketReference(result.id || null);
      setSubmitted(true);
    } else {
      setErrorMessage(result.message || GENERIC_SUBMIT_ERROR);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — clicking it is treated as an accidental dismiss. */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => requestClose("backdrop")}
        aria-hidden="true"
      />

      {/* Modal. Capped at 90vh with a scrolling body so the footer stays
          reachable on short viewports (landscape phones), matching
          VerifyModal and BuyLicensesModal. */}
      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="support-modal-title"
        className="relative flex w-full max-w-lg flex-col max-h-[90vh] rounded-xl bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50">
              <LifeBuoy className="h-4 w-4 text-blue-600" />
            </div>
            <h2 id="support-modal-title" className="text-base font-semibold text-gray-900">
              Contact support
            </h2>
          </div>
          <button
            type="button"
            onClick={() => requestClose("dismiss")}
            disabled={submitting}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {submitted ? (
          <>
            {/* role="status" so the confirmation is announced: the Send button
                the user was on unmounts when this replaces the form. */}
            <div role="status" className="flex-1 overflow-y-auto px-6 py-5 text-sm text-gray-600">
              <p className="font-medium text-gray-900">Thanks, your request has been sent.</p>
              <p className="mt-1">
                Our team will respond by email{reporterEmail ? ` to ${reporterEmail}` : ""}.
              </p>
              {ticketReference && (
                <p className="mt-3 text-xs text-gray-500">
                  Reference:{" "}
                  <span className="font-mono break-all text-gray-700">{ticketReference}</span>
                </p>
              )}
            </div>
            <div className="flex shrink-0 justify-end px-6 py-4 border-t border-gray-100">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="flex flex-col gap-4">
                <FormField label="Category" htmlFor="support-category" required>
                  <select
                    id="support-category"
                    value={category}
                    onChange={(changeEvent) => setCategory(changeEvent.target.value as SupportCategory)}
                    className="flex h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 transition-colors focus:border-blue-500/40 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  >
                    {SUPPORT_CATEGORIES.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <FormField
                  label="How can we help?"
                  htmlFor="support-message"
                  required
                  helperText={messageHelperText}
                >
                  <Textarea
                    id="support-message"
                    value={message}
                    maxLength={SUPPORT_MESSAGE_MAX_LENGTH}
                    placeholder="Describe the issue, question, or idea in as much detail as you can."
                    className="min-h-[140px]"
                    onChange={handleMessageChange}
                  />
                </FormField>

                {/* Identity is attached server-side from the signed-in session. */}
                <p className="text-xs text-gray-400">
                  Sent as <span className="font-medium text-gray-600">{reporterName}</span>
                  {reporterEmail ? <> ({reporterEmail})</> : null}.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 px-6 py-4 border-t border-gray-100">
              {/* Submission failures belong here, not on the message field: the
                  usual cause is the request, not the text the user typed. */}
              {errorMessage && (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                >
                  {errorMessage}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestClose("dismiss")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                  {submitting ? "Sending…" : "Send request"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
